import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import CookieConsent from "@/components/app/CookieConsent";
import AnalyticsRouterListener from "@/components/app/AnalyticsRouterListener";

import PublicLayout from "@/layouts/PublicLayout";
import ClientLayout from "@/layouts/ClientLayout";
import BrokerLayout from "@/layouts/BrokerLayout";
import AdminLayout from "@/layouts/AdminLayout";

import HomePage from "@/pages/public/HomePage";
import DirectoryPage from "@/pages/public/DirectoryPage";
import OrganizationPage from "@/pages/public/OrganizationPage";
import CategoryPage from "@/pages/public/CategoryPage";
import LoginPage from "@/pages/public/LoginPage";
import RegisterPage from "@/pages/public/RegisterPage";
import ForgotPasswordPage from "@/pages/public/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/public/ResetPasswordPage";
import AuthCallbackPage from "@/pages/public/AuthCallbackPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/public/PaymentReturnPages";
import PricingPage from "@/pages/public/PricingPage";
import HowItWorksPage from "@/pages/public/HowItWorksPage";
import BrokerLandingPage from "@/pages/public/BrokerLandingPage";
import { GuidesPage, GuideDetailPage, FAQPage, BlogPage, BlogDetailPage } from "@/pages/public/ContentPages";
import { AboutPage, ContactPage } from "@/pages/public/StaticPages";
import MentionsLegalesPage from "@/pages/public/MentionsLegalesPage";
import PolitiqueConfidentialitePage from "@/pages/public/PolitiqueConfidentialitePage";
import CGVPage from "@/pages/public/CGVPage";
import CGUPage from "@/pages/public/CGUPage";
import CookiesPage from "@/pages/public/CookiesPage";
import ModelesLettresPage from "@/pages/public/ModelesLettresPage";
import ReclamationPage from "@/pages/public/ReclamationPage";
import FinanceursPage from "@/pages/public/FinanceursPage";
import NotFoundPage from "@/pages/public/NotFoundPage";
import CaseSubmittedPage from "@/pages/public/CaseSubmittedPage";

import TerminationAssistantPage from "@/pages/TerminationAssistantPage";

import {
  ClientDashboard, CasesListPage, CaseDetailPage, EvidencePage,
  InvoicesPage, SupportPage, ProfilePage,
} from "@/pages/client/ClientPages";

import {
  BrokerDashboard, BrokerClientsPage, BrokerCasesPage, BrokerStatsPage, BrokerPaymentsPage,
} from "@/pages/broker/BrokerPages";

import {
  AdminDashboard, AdminUsersPage, AdminDirectoryPage, AdminCasesPage,
  AdminPaymentsPage, AdminShipmentsPage, AdminIntegrationsPage, AdminAuditPage,
} from "@/pages/admin/AdminPages";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminReviewQueuePage from "@/pages/admin/AdminReviewQueuePage";
import AdminMysendingboxPage from "@/pages/admin/AdminMysendingboxPage";
import AdminCaseDetailPage from "@/pages/admin/AdminCaseDetailPage";
import AdminUserDetailPage from "@/pages/admin/AdminUserDetailPage";

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsRouterListener />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="/annuaire" element={<DirectoryPage />} />
              <Route path="/categorie/:slug" element={<CategoryPage />} />
              <Route path="/organisme/:slug" element={<OrganizationPage />} />
              <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
              <Route path="/tarifs" element={<PricingPage />} />
              <Route path="/courtiers" element={<BrokerLandingPage />} />
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/guides/:slug" element={<GuideDetailPage />} />
              <Route path="/modeles-lettres" element={<ModelesLettresPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/aide" element={<Navigate to="/faq" replace />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/reclamation" element={<ReclamationPage />} />
              <Route path="/reclamations" element={<Navigate to="/reclamation" replace />} />
              <Route path="/connexion" element={<LoginPage />} />
              <Route path="/inscription" element={<RegisterPage />} />
              <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
              <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/paiement/succes" element={<PaymentSuccessPage />} />
              <Route path="/paiement/annule" element={<PaymentCancelPage />} />
              <Route path="/financeurs" element={<FinanceursPage />} />

              {/* Legal (canonical URLs) */}
              <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
              <Route path="/cgu" element={<CGUPage />} />
              <Route path="/cgv" element={<CGVPage />} />
              <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              {/* Legacy redirect */}
              <Route path="/confidentialite" element={<Navigate to="/politique-confidentialite" replace />} />

              {/* Case submission success */}
              <Route path="/dossier/soumis" element={<CaseSubmittedPage />} />

              {/* Blog removed — permanent redirect to FAQ */}
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogDetailPage />} />
            </Route>

            {/* Assistant */}
            <Route element={<PublicLayout />}>
              <Route path="/resilier" element={<TerminationAssistantPage />} />
              <Route path="/resilier/:caseId" element={<TerminationAssistantPage />} />
            </Route>

            {/* Client */}
            <Route element={<ClientLayout />}>
              <Route path="/espace" element={<ClientDashboard />} />
              <Route path="/espace/dossiers" element={<CasesListPage />} />
              <Route path="/espace/dossiers/:id" element={<CaseDetailPage />} />
              <Route path="/espace/preuves" element={<EvidencePage />} />
              <Route path="/espace/factures" element={<InvoicesPage />} />
              <Route path="/espace/support" element={<SupportPage />} />
              <Route path="/espace/profil" element={<ProfilePage />} />
            </Route>

            {/* Broker */}
            <Route element={<BrokerLayout />}>
              <Route path="/courtier" element={<BrokerDashboard />} />
              <Route path="/courtier/clients" element={<BrokerClientsPage />} />
              <Route path="/courtier/dossiers" element={<BrokerCasesPage />} />
              <Route path="/courtier/statistiques" element={<BrokerStatsPage />} />
              <Route path="/courtier/paiements" element={<BrokerPaymentsPage />} />
            </Route>

            {/* Admin */}
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/utilisateurs" element={<AdminUsersPage />} />
              <Route path="/admin/users/:user_id" element={<AdminUserDetailPage />} />
              <Route path="/admin/annuaire" element={<AdminDirectoryPage />} />
              <Route path="/admin/dossiers" element={<AdminCasesPage />} />
              <Route path="/admin/cases/:case_id" element={<AdminCaseDetailPage />} />
              <Route path="/admin/dossiers/a-verifier" element={<AdminReviewQueuePage />} />
              <Route path="/admin/paiements" element={<AdminPaymentsPage />} />
              <Route path="/admin/envois" element={<AdminShipmentsPage />} />
              <Route path="/admin/postal/mysendingbox" element={<AdminMysendingboxPage />} />
              <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
              <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
              <Route path="/admin/audit" element={<AdminAuditPage />} />
            </Route>

            {/* 404 catch-all */}
            <Route path="*" element={<PublicLayout />}>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
          <CookieConsent />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
